// Kanban : une frise à la fois. Les colonnes sont les étapes de cette frise.
import { DossierViewSelect } from './DossierViewSelect';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useDossierRegistrySettings } from '../../hooks/queries/useCabinetSettings';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Briefcase, Calendar } from 'lucide-react';
import { cn } from '../../lib/cn';
import { SEMI_FRESH } from '../../lib/queryPresets';
import { Spinner } from '../ui/Spinner';
import { useDossiers, useTransitionFriseStep } from '../../hooks/queries/useDossiers';
import { type Dossier } from '../../services/dossiers/dossierService';
import { loadParcoursCatalog } from '../../services/dossiers/friseService';
import { todayInputValue } from '../../services/dossiers/stageDate';
import {
  boardFrises,
  columnDossiers,
  columnsFor,
  dossierBoardRef,
  rememberedFriseId,
} from '../../lib/dossiers/friseBoard';
import { TypeBadge } from './dossierBadges';

import { formatAppDate } from '../../lib/dateFormat';
const KANBAN_FRISE_KEY = 'cime.dossiers.kanbanFrise';

const COLUMN_HUE = [
  'border-blue-200 bg-blue-50/40',
  'border-indigo-200 bg-indigo-50/40',
  'border-amber-200 bg-amber-50/40',
  'border-orange-200 bg-orange-50/40',
  'border-emerald-200 bg-emerald-50/40',
  'border-purple-200 bg-purple-50/40',
  'border-cyan-200 bg-cyan-50/40',
];

function columnHue(index: number, last: boolean): string {
  if (last) return 'border-neutral-300 bg-neutral-100/60';
  return COLUMN_HUE[index % COLUMN_HUE.length] ?? COLUMN_HUE[0]!;
}

/** Vue en colonnes : accessible seulement si le cabinet l'a activée. */
export function DossiersKanbanPage() {
  const { data: registry } = useDossierRegistrySettings();
  if (!registry) return null;
  if (!registry.board) return <Navigate to="/dossiers" replace />;
  return <DossiersKanbanBoard />;
}

function DossiersKanbanBoard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: dossiers, isLoading } = useDossiers({ include_closed: true });
  const { data: catalog } = useQuery({
    ...SEMI_FRESH,
    queryKey: ['dossier-parcours-catalog'],
    queryFn: loadParcoursCatalog,
  });
  const transition = useTransitionFriseStep();
  const frises = useMemo(() => boardFrises(catalog ?? []), [catalog]);
  const [friseId, setFriseId] = useState<string | null>(null);

  useEffect(() => {
    if (!frises.length) return;
    const fromUrl = searchParams.get('frise');
    const stored = fromUrl ?? localStorage.getItem(KANBAN_FRISE_KEY);
    const next = rememberedFriseId(frises, stored);
    setFriseId(next);
    if (next) localStorage.setItem(KANBAN_FRISE_KEY, next);
  }, [frises, searchParams]);

  function chooseFrise(id: string) {
    setFriseId(id);
    localStorage.setItem(KANBAN_FRISE_KEY, id);
    setSearchParams({ frise: id });
  }

  const columns = columnsFor(frises, friseId);
  const placed = useMemo(() => {
    const cards = (dossiers ?? []).map((dossier) => ({
      dossier,
      ...dossierBoardRef(dossier),
    }));
    return friseId ? columnDossiers(cards, friseId) : new Map<number, typeof cards>();
  }, [dossiers, friseId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  function onDragEnd(event: DragEndEvent) {
    if (!friseId) return;
    const dossierId = String(event.active.id);
    const target = Number(event.over?.id);
    if (!Number.isInteger(target)) return;
    const dossier = dossiers?.find((item) => item.id === dossierId);
    if (!dossier || dossierBoardRef(dossier).position === target) return;
    if (dossierBoardRef(dossier).friseTemplateId !== friseId) return;
    transition.mutate({ id: dossierId, position: target, effectiveOn: todayInputValue() });
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center gap-2 px-4 pt-3">
        <label htmlFor="kanban-frise" className="text-xs font-medium text-cime-text-muted">
          Frise
        </label>
        <select
          id="kanban-frise"
          value={friseId ?? ''}
          onChange={(event) => chooseFrise(event.target.value)}
          className="text-sm border border-neutral-200 rounded-md bg-white px-2 py-1 text-cime-text-primary"
        >
          {frises.map((frise) => (
            <option key={frise.id} value={frise.id}>
              {frise.familyName} — {frise.name}
            </option>
          ))}
        </select>
        <span className="flex-1" aria-hidden />
        <DossierViewSelect />
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : (
        <div className="flex-1 overflow-x-auto overflow-y-hidden bg-neutral-50">
          <DndContext sensors={sensors} onDragEnd={onDragEnd}>
            <div className="flex gap-3 p-4 h-full min-w-max">
              {columns.map((column, index) => (
                <KanbanColumn
                  key={column.position}
                  position={column.position}
                  label={column.label}
                  hue={columnHue(index, index === columns.length - 1)}
                  dossiers={(placed.get(column.position) ?? []).map((row) => row.dossier)}
                  onCardClick={(id) => navigate(`/dossiers/${id}`)}
                />
              ))}
            </div>
          </DndContext>
        </div>
      )}
    </div>
  );
}

function KanbanColumn({
  position, label, hue, dossiers, onCardClick,
}: {
  position: number;
  label: string;
  hue: string;
  dossiers: Dossier[];
  onCardClick: (id: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: String(position) });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col w-72 flex-shrink-0 rounded-cime border bg-white transition-colors',
        hue,
        isOver && 'ring-2 ring-cime-primary/40',
      )}
    >
      <header className="flex items-center justify-between px-3 py-2 border-b border-neutral-200/60">
        <h2 className="text-sm font-semibold text-cime-text-primary">{label}</h2>
        <span className="text-xs font-medium text-cime-text-muted bg-white border border-neutral-200 rounded px-1.5 py-0.5 tabular-nums">
          {dossiers.length}
        </span>
      </header>
      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[100px]">
        {dossiers.length === 0 && (
          <p className="text-[11px] italic text-cime-text-muted text-center pt-4">
            Aucun dossier
          </p>
        )}
        {dossiers.map((dossier) => (
          <DraggableCard key={dossier.id} dossier={dossier} onClick={() => onCardClick(dossier.id)} />
        ))}
      </div>
    </div>
  );
}

function DraggableCard({ dossier, onClick }: { dossier: Dossier; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dossier.id,
  });

  const style: React.CSSProperties | undefined = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.7 : 1,
        zIndex: isDragging ? 50 : undefined,
      }
    : undefined;

  return (
    <article
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={cn(
        'rounded-md border border-neutral-200 bg-white p-2.5 shadow-sm hover:shadow-md transition cursor-grab active:cursor-grabbing',
        dossier.archived_at && 'opacity-60',
      )}
    >
      <div className="flex items-center gap-1.5 text-[11px] text-cime-text-muted mb-1">
        <Briefcase className="w-3 h-3" />
        {dossier.number}
      </div>
      <p className="text-sm font-medium text-cime-text-primary line-clamp-2 mb-1.5">
        {dossier.title}
      </p>
      <div className="flex items-center gap-1.5 flex-wrap">
        <TypeBadge type={dossier.type_affaire} className="text-[10px]" />
        {dossier.next_hearing_at && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-cime-text-muted tabular-nums">
            <Calendar className="w-2.5 h-2.5" />
            {formatAppDate(dossier.next_hearing_at, { year: false })}
          </span>
        )}
      </div>
    </article>
  );
}
